/**
 * Auth types and enums.
 * This file exists to break circular dependencies between brightback-auth.slice.ts and brightback-auth.ts
 */

import {
  type ChargebeeGetSignInUrlArgs,
  type ChargebeeSignInArgs,
} from '@bbkAdminUtils/auth-chargebee';
import { type DevSignInArgs } from '@bbkAdminUtils/auth-dev';

export enum SignInType {
  DEV = 'DEV',
  CHARGEBEE = 'CHARGEBEE',
}

/**
 * Non-secret session metadata returned by the backend session endpoints.
 *
 * The credential (JWT) lives only in the backend-owned HttpOnly cookie; the
 * frontend never receives, stores, or parses a JWT. All the client keeps is the
 * session `type`, which a few routes branch on. Authentication itself is
 * derived from `/api/current_user`, and how long the session has left is no
 * longer the client's business: the backend extends the cookie on the requests
 * the app is already making.
 */
export type SessionMetadata = {
  type: SignInType;
  // Authenticated email, only present on the CB IDP exchange response. Used
  // transiently to warn when the completed login differs from the address the
  // user started with (`originalEmail`); it is never persisted.
  email?: string;
};

/** @deprecated Backward-compatible alias; the payload is metadata, not tokens. */
export type SignInTokens = SessionMetadata;

/**
 * Raw session metadata as serialized by the backend (`type` is a lowercase tag).
 * `exp` is still on the wire and still describes the cookie the backend just
 * wrote; nothing on the client acts on it.
 */
export type SessionMetadataResponse = {
  type: 'cbidp' | 'dev';
  exp: number;
  email?: string;
};

export type SignInArgs =
  | ({
      type: SignInType.DEV;
    } & DevSignInArgs)
  | ({
      type: SignInType.CHARGEBEE;
    } & ChargebeeSignInArgs);

export type GetSignInUrlArgs = {
  type: SignInType.CHARGEBEE;
} & ChargebeeGetSignInUrlArgs;
